import { inject, Injectable } from '@angular/core';
import {
    AvatarInfoService,
    UserFacadeService,
} from '@wdx/clmi/api-services/services';
import {
    DynamicFormsManagementService,
    ModalManagementService,
} from '@wdx/clmi/ui';
import { PartyToAvatarPipe } from '@wdx/clmi/utils/pipes';
import {
    EntityRouteService,
    SystemSettingsService,
} from '@wdx/clmi/utils/services';
import {
    AG_GRID_VALUE_GETTER_BY_FIELD,
    BadgeRendererComponent,
    BadgeRendererValue,
    BooleanRendererComponent,
    DATE_CELL_COL_DEFS,
    DATE_CELL_VALUE_FORMATTER,
    EnumRendererComponent,
    EnumRendererValue,
    FileIconRendererComponent,
    FileIconRendererValue,
    IconWithTextCellRendererComponent,
    IconWithTextCellRendererParams,
    IdentitiesRendererComponent,
    LifecycleRendererComponent,
    LinkRendererComponent,
    RouterLinkRendererValue,
    SHORT_CURRENCY_VALUE_FORMATTER,
    SwitchRendererComponent,
    SwitchRendererValue,
    TRANSLATE_CELL_VALUE,
    WdxColDef,
} from '@wdx/shared/components/ag-grid';
import { FilesFacadeService } from '@wdx/shared/infrastructure/file-io';
import {
    FormFrameworkEvent,
    TelephoneService,
} from '@wdx/shared/infrastructure/form-framework';
import {
    ActionType,
    ComparisonOperator,
    Country,
    Document,
    ElectronicAddress,
    EntityPermissionType,
    FileIndex,
    FilterFieldDefinition,
    LocalisedString,
    Opportunity,
    PhoneAddress,
    Pipeline,
    Rag,
    StatusType,
    SystemEntity,
    ViewColumn,
    ViewColumnType,
    ViewThreshold,
} from '@wdx/shared/swagger-gen';
import {
    ActionButton,
    ActionButtonMode,
    AUDIT_DIFF_MODAL,
    EntityDisplayNamePipe,
    FORM_HISTORY_MODAL,
    MenuItemAction,
    NavItem,
    ShortCurrencyPipe,
    TranslationsService,
    UTILS_ICON_ARROW_UP,
    UTILS_ICON_CHECK,
    UTILS_THIN_ARROW_DOWN,
    WdxNumberService,
    WdxDateFormat,
    WdxDateTimeService,
} from '@wdx/shared/utils';
import {
    CellRendererSelectorFunc,
    ICellRendererParams,
    ValueFormatterParams,
    ValueGetterParams,
} from 'ag-grid-community';
import * as R from 'ramda';
import { filter, map, Observable, switchMap, take, takeUntil } from 'rxjs';
import { DynamicPagesFacade } from '../+state/dynamic-pages.facade';
import {
    ASSIGN_TO_MENU_ITEM,
    DELETE_MENU_ITEM,
    DOWNLOAD_MENU_ITEM,
    EDIT_MENU_ITEM,
    HISTORY_MENU_ITEM,
    MOVE_TO_STAGE_MENU_ITEM,
    PRE_POST_MENU_ITEM,
    PREVIEW_MENU_ITEM,
    VIEW_MENU_ITEM,
    VISIBILITY_MENU_ITEM,
} from '../../../constants/menu.constants';
import { ActionButtonCellRendererComponent } from '../../../shared/components/tables/cell-renderers/action-button-cell-renderer/action-button-cell-renderer.component';
import {
    AVATAR_CELL_RENDERER_CONFIG,
    AVATAR_CELL_RENDERER_PARAMS,
    AVATAR_CELL_RENDERER_SELECTOR,
} from '../../../shared/components/tables/cell-renderers/helpers/avatar-renderer-params/avatar-renderer-params';
import { ReferenceCellRendererComponent } from '../../../shared/components/tables/cell-renderers/reference-cell-renderer/reference-cell-renderer.component';
import { AccessVisibilityService } from '../../../shared/features/access-visibility/shared/services/access-visibility.service';
import { AssignToService } from '../../../shared/features/assign-to/services/assign-to.service';
import {
    DocumentPreviewMode,
    DocumentsModalService,
} from '../../../shared/features/documents';
import { KanbanStagesFacadeService } from '../../../state/kanban-stages/kanban-stages-facade.service';
import { OpportunitiesFacadeService } from '../../../state/opportunities/opportunities-facade.service';
import { NavigationService } from '../../../shared/services/navigation/navigation.service';
import {
    FILTER_QUERY_SEARCH,
    FILTER_QUERY_SEARCH_CLEAR,
    FILTER_SARCH_BAR_CONFIG,
} from '@wdx/clmi/api-services/models';

@Injectable()
export class DynamicEntityHelperService {
    private translationsService = inject(TranslationsService);
    private dateTimeService = inject(WdxDateTimeService);
    private avatarInfoService = inject(AvatarInfoService);
    private partyToAvatarPipe = inject(PartyToAvatarPipe);
    private dynamicFormsManagementService = inject(
        DynamicFormsManagementService,
    );
    private documentPreviewService = inject(DocumentsModalService);
    private filesFacadeService = inject(FilesFacadeService);
    private entityRouteService = inject(EntityRouteService);
    private kanbanStagesFacade = inject(KanbanStagesFacadeService);
    private opportunitiesFacadeService = inject(OpportunitiesFacadeService);
    private dynamicPagesFacade = inject(DynamicPagesFacade);
    private assignToService = inject(AssignToService);
    private accessVisibilityService = inject(AccessVisibilityService);
    private telephoneService = inject(TelephoneService);
    private userFacadeService = inject(UserFacadeService);
    private navigationService = inject(NavigationService);
    private modalManagementService = inject(ModalManagementService);
    private numberService = inject(WdxNumberService);
    public businessDayLengthHours = inject(SystemSettingsService).get()
        ?.BusinessDayLengthHours;

    public localeCode = this.userFacadeService?.me?.locale?.code;

    public createColDef(
        entityType: SystemEntity,
        viewColumn: ViewColumn,
        config: {
            formName?:
                | null
                | string
                | ((params: ICellRendererParams) => string | undefined);
            hasDetailPage?: boolean;
            disable?: boolean;
            parentRoute?: string[];
            isEmbeddedPage?: boolean;
            disableSelfLink?: boolean;
        },
        countries: Country[],
        toggleCallBack?: (on: boolean, id: string) => void,
    ): WdxColDef {
        let colDef = {
            headerName: viewColumn.name
                ? this.translationsService.translateTokenisedString(
                      viewColumn.name,
                  )
                : viewColumn.path,
            wrapHeaderText: true,
            autoHeaderHeight: true,
            colId: viewColumn.path,
            field: viewColumn.path,
            sortable: viewColumn.sortable,
            // When size is 0, don't set flex to allow auto-sizing to work
            // This enables "min-content" behavior
            ...(viewColumn.size !== 0 && {
                flex: R.isNotNil(viewColumn.size) ? viewColumn.size : 1,
            }),
            minWidth: viewColumn.minSize ?? 75,
            maxWidth: viewColumn.maxSize,
            wdx: {
                sortTarget: viewColumn.sortKey || viewColumn.path,
            },
            viewColumn,
        } as WdxColDef;
        switch (viewColumn?.type) {
            case ViewColumnType.Answer:
                colDef.valueGetter = (params: ValueGetterParams) =>
                    params.data[params.colDef.field]?.label;
                break;

            case ViewColumnType.Enum:
                colDef.cellRendererSelector = (
                    params: ICellRendererParams,
                ) => ({
                    component: EnumRendererComponent,
                    params: {
                        value: {
                            type: viewColumn.enumType,
                            value: AG_GRID_VALUE_GETTER_BY_FIELD(params),
                        } as EnumRendererValue,
                    },
                });
                break;

            case ViewColumnType.Number:
                break;

            case ViewColumnType.Boolean:
                colDef.cellRendererSelector = (params: ICellRendererParams) => {
                    if (params.node.group) {
                        return;
                    }
                    return {
                        component: BooleanRendererComponent,
                        params: {
                            value: params.data[viewColumn.path],
                        },
                    };
                };
                break;

            case ViewColumnType.Toggle:
                colDef.cellRendererSelector = (
                    params: ICellRendererParams,
                ) => ({
                    component: SwitchRendererComponent,
                    params: {
                        value: {
                            disabled: config.disable,
                            toggled: params.data[viewColumn.path],
                            toggleCallBack,
                        } as SwitchRendererValue,
                    },
                });
                break;

            case ViewColumnType.Activity:
                colDef = { ...colDef, ...DATE_CELL_COL_DEFS };
                colDef.field = viewColumn.path + '.date';
                colDef.valueFormatter = DATE_CELL_VALUE_FORMATTER(
                    this.dateTimeService,
                    WdxDateFormat.AbsoluteDateTime,
                );

                break;

            case ViewColumnType.ClientSummary:
                colDef.minWidth = viewColumn.minSize ?? 85;
                colDef.cellRendererSelector = (
                    params: ICellRendererParams,
                ) => ({
                    component: LifecycleRendererComponent,
                    params: {
                        value: params.data?.clientStatus?.clientLifecycle,
                    },
                });
                break;

            case ViewColumnType.Reference:
                colDef.field = viewColumn.path;
                colDef.cellRenderer = ReferenceCellRendererComponent;
                break;

            case ViewColumnType.Identities:
                colDef.field = viewColumn.path;
                colDef.cellRenderer = IdentitiesRendererComponent;
                break;

            case ViewColumnType.Date:
                colDef.valueFormatter = DATE_CELL_VALUE_FORMATTER(
                    this.dateTimeService,
                    WdxDateFormat.AbsoluteDate,
                );
                break;

            case ViewColumnType.DateTime:
                colDef.valueFormatter = DATE_CELL_VALUE_FORMATTER(
                    this.dateTimeService,
                    WdxDateFormat.AbsoluteDateTime,
                );
                break;

            case ViewColumnType.Text:
                colDef.valueFormatter = TRANSLATE_CELL_VALUE(
                    this.translationsService,
                );
                break;

            case ViewColumnType.PhoneAddress:
                colDef.valueFormatter = (params) => {
                    const value = AG_GRID_VALUE_GETTER_BY_FIELD(
                        params,
                    ) as PhoneAddress;
                    const country = value.country
                        ? countries.find(
                              (c) => c.isoCode === value.country?.isoCode,
                          ) || value.country
                        : undefined;
                    return value?.number
                        ? this.telephoneService.formatPhoneNumber(
                              value.number,
                              value.phoneType,
                              country,
                          )
                        : '';
                };
                break;

            case ViewColumnType.ElectronicAddress:
                colDef.valueFormatter = (params) => {
                    const value = AG_GRID_VALUE_GETTER_BY_FIELD(
                        params,
                    ) as ElectronicAddress;
                    return value.emailAddress;
                };
                break;

            case ViewColumnType.Self:
                {
                    // TODO: the api should tell us the path to the ID, when is does we need to remove this hardcoded lookup
                    const idGetter = (params) => {
                        switch (entityType) {
                            case SystemEntity.Lead:
                                return params.data?.party?.id;
                            case SystemEntity.AnswerType:
                                return params.data?.code;
                            default:
                                return params.data?.id;
                        }
                    };

                    if (viewColumn?.showAvatar) {
                        colDef.cellRendererParams = AVATAR_CELL_RENDERER_PARAMS(
                            this.avatarInfoService,
                            entityType,
                        );
                        colDef.cellRendererSelector = (params) => {
                            const id = idGetter(params);

                            const partyStub = {
                                avatarUrl: params.data?.avatarUrl,
                                id,
                                name: params.data?.name,
                            };

                            const { callback, url } = this.selfCallbackAndUrl(
                                config,
                                id,
                                entityType,
                                params,
                            );
                            return AVATAR_CELL_RENDERER_SELECTOR(
                                this.partyToAvatarPipe,
                                {
                                    showLabel: true,
                                    url: config.disable ? undefined : url,
                                    badge:
                                        viewColumn?.showStatusIndicator &&
                                        params.data?.status ===
                                            StatusType.Inactive
                                            ? {
                                                  label: params.data?.status,
                                                  color: 'info',
                                              }
                                            : undefined,
                                    linkClickCallback: callback,
                                },
                                partyStub,
                            )(params);
                        };
                    } else {
                        colDef.cellRendererSelector = (params) => {
                            const id = idGetter(params);

                            const { callback, url } = this.selfCallbackAndUrl(
                                config,
                                id,
                                entityType,
                                params,
                            );
                            const fieldValue = params.data[colDef.field];
                            return {
                                component: BadgeRendererComponent,
                                params: {
                                    value: {
                                        preLabel:
                                            (fieldValue as LocalisedString)
                                                ?.value ?? fieldValue,
                                        preLabelUrl: config.disable
                                            ? undefined
                                            : url,
                                        label: params.data.status,
                                        mapping: {
                                            inactive: 'info',
                                        },
                                        preLabelClickCallback: callback,
                                        disableBadge:
                                            !viewColumn?.showStatusIndicator ||
                                            params.data.status !==
                                                StatusType.Inactive,
                                    } as BadgeRendererValue,
                                },
                            };
                        };
                    }
                }
                break;

            case ViewColumnType.Status:
                colDef.cellRendererSelector = (params) => {
                    const value = AG_GRID_VALUE_GETTER_BY_FIELD(params);
                    const label = viewColumn.enumType
                        ? this.translationsService.translateTokenisedString(
                              `ENUM_${viewColumn.enumType?.toUpperCase()}_${value?.toUpperCase()}`,
                          )
                        : this.translationsService.translateTokenisedString(
                              `ENTITY_LIST_STATUS_${value?.toUpperCase()}`,
                          );
                    return {
                        component: BadgeRendererComponent,
                        params: {
                            value: {
                                label,
                                untranslatedLabel: value,
                                mapping: {
                                    active: 'success',
                                    blocked: 'danger',
                                    cancelled: 'none',
                                    closing: 'warning',
                                    draft: 'info',
                                    inactive: 'none',
                                    proposed: 'info',
                                },
                            } as BadgeRendererValue,
                        },
                    };
                };
                break;

            case ViewColumnType.Avatar:
                colDef = {
                    ...colDef,
                    ...AVATAR_CELL_RENDERER_CONFIG(
                        this.avatarInfoService,
                        this.partyToAvatarPipe,
                        true,
                    ),
                };
                break;

            case ViewColumnType.FileIndex:
                colDef.cellRendererSelector = (params: ICellRendererParams) => {
                    if (params.node.group) {
                        return;
                    }
                    const fileIndex = params.data?.fileIndex as FileIndex;
                    const entityId = params.data?.id;
                    const canPreviewInline =
                        fileIndex?.contentType === 'application/pdf' ||
                        fileIndex?.contentType?.startsWith('image/');
                    const clickCallback =
                        canPreviewInline && entityId
                            ? () =>
                                  this.onPreviewDocument(
                                      { fileIndex },
                                      entityId,
                                      entityType,
                                  )
                            : undefined;
                    return {
                        component: FileIconRendererComponent,
                        params: {
                            value: {
                                label: fileIndex?.name || params.data?.name,
                                fileIndex,
                                clickCallback,
                            } as FileIconRendererValue,
                        },
                    };
                };

                break;

            case ViewColumnType.Currency:
                colDef.valueFormatter = SHORT_CURRENCY_VALUE_FORMATTER();
                break;

            case ViewColumnType.Duration:
                colDef.valueFormatter = (params: ValueFormatterParams) => {
                    const value =
                        params.value !== undefined && params.value !== ''
                            ? params.value
                            : AG_GRID_VALUE_GETTER_BY_FIELD(params);

                    return typeof value === 'number'
                        ? this.formatDurationValue(value)
                        : '';
                };
                break;

            case ViewColumnType.Pipeline:
                colDef.valueFormatter = (params: ValueFormatterParams) => {
                    const { value, count } = AG_GRID_VALUE_GETTER_BY_FIELD(
                        params,
                    ) as Pipeline;
                    const formattedValue = value
                        ? new ShortCurrencyPipe().transform(
                              value?.value,
                              value?.isoCode,
                          )
                        : '';
                    return value?.value ? `${formattedValue} (${count})` : '';
                };
                break;

            case ViewColumnType.Url:
                colDef.cellRendererSelector = (
                    params: ICellRendererParams,
                ) => ({
                    component: LinkRendererComponent,
                    params: {
                        value: {
                            url: AG_GRID_VALUE_GETTER_BY_FIELD(params),
                            label: AG_GRID_VALUE_GETTER_BY_FIELD(params),
                            externalLink: true,
                        } as RouterLinkRendererValue,
                    },
                });
                break;

            case ViewColumnType.Icon:
                colDef.cellRendererSelector = (
                    params: ICellRendererParams,
                ) => ({
                    component: IconWithTextCellRendererComponent,
                    params: {
                        value: {
                            icon: AG_GRID_VALUE_GETTER_BY_FIELD(params),
                        } as IconWithTextCellRendererParams,
                    },
                });
                break;

            default:
                break;
        }

        const thresholds = (
            viewColumn as {
                thresholds?: ViewThreshold[] | null;
            }
        ).thresholds;

        if (thresholds?.length) {
            colDef.cellRendererSelector = this.thresholdCellRenderer(
                viewColumn,
                colDef,
                thresholds,
            );
        }

        return colDef.cellRendererSelector
            ? { ...colDef, field: undefined }
            : colDef;
    }

    public actionsCellRenderer(
        entityType: SystemEntity,
        formName:
            | string
            | ((params: ICellRendererParams) => string | undefined),
        deleteCallbackProvider: (
            entityType: SystemEntity,
            entityId: string,
            name: string,
        ) => () => void,
        destroyed$: Observable<boolean>,
        refreshCallback: () => void,
        queryEntity?: string,
        formEventHandler?: (event: FormFrameworkEvent) => void,
    ): CellRendererSelectorFunc {
        return (params: ICellRendererParams) => {
            if (params.node.group) {
                return;
            }

            // If the grid has a column of type 'ViewColumnType.Self' then use the value of that column for the name variable
            const selfColumn = params.api
                .getColumns()
                ?.find(
                    (column) =>
                        (column.getColDef() as WdxColDef)?.viewColumn?.type ===
                        ViewColumnType.Self,
                );
            const selfFieldName =
                selfColumn?.getColDef()?.field || selfColumn?.getColId();
            const selfFieldValue = selfFieldName
                ? params.data[selfFieldName]
                : undefined;

            const entityId =
                params.data.id || params.data.code || params.data.identifier;
            const name =
                selfFieldValue ||
                params.data.name ||
                params.data.value ||
                entityId;

            const visibilityMenuItem = {
                permission: EntityPermissionType.ManageRecordAccess,
                ...VISIBILITY_MENU_ITEM,
                callback: () => {
                    this.accessVisibilityService.open(entityType, entityId);
                },
            };

            const assignToMenuItem = {
                permission: EntityPermissionType.Assign,
                ...ASSIGN_TO_MENU_ITEM,
                cySelector: `option-assignTo-${entityId}`,
                callback: () => {
                    this.assignToService
                        .openAssignToModal(entityType, entityId)
                        .pipe(
                            takeUntil(destroyed$),
                            switchMap(() =>
                                this.assignToService
                                    .assignToComplete$(entityType, entityId)
                                    .pipe(takeUntil(destroyed$)),
                            ),
                        )
                        .subscribe(() =>
                            this.dynamicPagesFacade.createNewEvent(entityType),
                        );
                },
            };

            const formId =
                typeof formName === 'function' ? formName(params) : formName;
            const formRelatedMenuItems = formId
                ? [
                      {
                          permission: EntityPermissionType.View,
                          notPermission: EntityPermissionType.Edit,
                          ...VIEW_MENU_ITEM,
                          callback: () => {
                              this.dynamicFormsManagementService.openDynamicFormModal(
                                  {
                                      formId,
                                      entityId,
                                      readonlyMode: true,
                                  },
                              );
                          },
                      },
                      {
                          permission: EntityPermissionType.Edit,
                          ...EDIT_MENU_ITEM,
                          callback: () => {
                              this.dynamicFormsManagementService
                                  .openDynamicFormModal({
                                      formId,
                                      entityId,
                                  })
                                  .closed.pipe(takeUntil(destroyed$))
                                  .subscribe(formEventHandler);
                          },
                      },
                      {
                          permission: EntityPermissionType.Edit,
                          ...HISTORY_MENU_ITEM,
                          callback: () => {
                              this.modalManagementService.openModalWithId(
                                  FORM_HISTORY_MODAL,
                                  {
                                      formId,
                                      entityId,
                                  },
                              );
                          },
                      },
                  ]
                : [];

            const auditEventMenuItems =
                entityType === SystemEntity.AuditEvent
                    ? [
                          {
                              ...PRE_POST_MENU_ITEM,
                              callback: () => {
                                  this.modalManagementService.openModalWithId(
                                      AUDIT_DIFF_MODAL,
                                      {
                                          auditEventId: entityId,
                                      },
                                  );
                              },
                          },
                      ]
                    : [];

            const dropdownMenu = [
                ...auditEventMenuItems,
                ...(formId || entityType === SystemEntity.Case
                    ? [visibilityMenuItem, assignToMenuItem]
                    : []),
                ...formRelatedMenuItems,
            ];

            const opportunityActions = this.getOpportunitiesActions(
                entityType,
                params.data,
            );
            const downloadAndPreviewActions = params.data?.fileIndex
                ? this.getDownloadandPreviewActions(
                      params.data?.fileIndex as FileIndex,
                      entityId,
                      entityType,
                      queryEntity || entityType,
                  )
                : [];

            return {
                component: ActionButtonCellRendererComponent,
                params: {
                    value: {
                        mode: ActionButtonMode.DropdownButtonMenu,
                        cySelector: `btn-actions-cell-renderer-action`,
                        operationsSetup: {
                            entityType,
                            entityId: params.data.id || params.data.code,
                        },
                        dropdownMenu: [
                            ...dropdownMenu,
                            ...downloadAndPreviewActions,
                            ...opportunityActions,
                            {
                                permission: EntityPermissionType.Delete,
                                ...DELETE_MENU_ITEM,
                                callback: deleteCallbackProvider(
                                    entityType,
                                    entityId,
                                    name,
                                ),
                            },
                        ],
                        actionCallbacks: [
                            {
                                type: ActionType.Patch,
                                callback: refreshCallback,
                            },
                        ],
                    } as ActionButton,
                },
            };
        };
    }

    public getSearchFilterDefinition(
        filterDefs: FilterFieldDefinition[],
    ): FilterFieldDefinition {
        const identityConfig = filterDefs.find(
            (fd) => fd.name === FILTER_SARCH_BAR_CONFIG,
        );
        if (identityConfig) {
            return identityConfig;
        }
        const searchConfigs = filterDefs.filter((fd) =>
            [FILTER_QUERY_SEARCH_CLEAR, FILTER_QUERY_SEARCH].includes(fd.name),
        );
        if (searchConfigs?.length === 2) {
            return {
                searchBarType: 'DropDownSelector',
                searchBarOptions: [
                    {
                        name:
                            searchConfigs.find(
                                (fd) => fd.name === FILTER_QUERY_SEARCH,
                            )?.displayName?.key || 'DEFAULT_SEARCH_BAR_OPTION',
                        filterName: FILTER_QUERY_SEARCH,
                    },
                    {
                        name:
                            searchConfigs.find(
                                (fd) => fd.name === FILTER_QUERY_SEARCH_CLEAR,
                            )?.displayName?.key ||
                            'DEFAULT_SEARCH_BAR_OPTION_CLEAR',
                        filterName: FILTER_QUERY_SEARCH_CLEAR,
                    },
                ],
            } as FilterFieldDefinition;
        }
        return {};
    }

    private getOpportunitiesActions(
        entityType: SystemEntity,
        data: Opportunity,
    ) {
        return entityType === SystemEntity.Opportunity
            ? [
                  {
                      ...MOVE_TO_STAGE_MENU_ITEM,
                      linkedMenu$: this.kanbanStagesFacade.kanbanStages$().pipe(
                          take(1),
                          map((kanbanStages) =>
                              kanbanStages
                                  .filter(({ code }) => code !== data.stageCode)
                                  .map(({ name, code }, i) => ({
                                      label: name,
                                      value: MenuItemAction.MoveToStage,
                                      data: {
                                          code: code,
                                          index: i,
                                      },
                                      callback: () => {
                                          this.opportunitiesFacadeService.moveToStageAction(
                                              code,
                                              data,
                                          );
                                          this.opportunitiesFacadeService
                                              .getActiveMoveToStage()
                                              .pipe(
                                                  filter(
                                                      (stage) => stage === null,
                                                  ),
                                                  take(1),
                                              )
                                              .subscribe(() => {
                                                  this.dynamicPagesFacade.createNewEvent(
                                                      entityType,
                                                  );
                                              });
                                      },
                                  })),
                          ),
                      ),
                      permission: EntityPermissionType.Edit,
                  },
              ]
            : [];
    }

    private getDownloadandPreviewActions(
        fileIndex: FileIndex,
        entityId: string,
        entityType: SystemEntity,
        queryEntity?: string,
    ): NavItem[] {
        const canPreviewInline =
            fileIndex?.contentType === 'application/pdf' ||
            fileIndex?.contentType?.startsWith('image/');
        const downloadAction = {
            ...DOWNLOAD_MENU_ITEM,
            permission: EntityPermissionType.Download,
            callback: () => {
                this.onDownloadDocument(entityId, queryEntity, fileIndex?.name);
            },
        };

        return canPreviewInline
            ? [
                  {
                      ...PREVIEW_MENU_ITEM,
                      permission: EntityPermissionType.Download,
                      callback: () => {
                          this.onPreviewDocument(
                              { fileIndex },
                              entityId,
                              entityType,
                          );
                      },
                  },
                  downloadAction,
              ]
            : [downloadAction];
    }

    private onPreviewDocument(
        document: Document,
        entityId: string,
        entityType: SystemEntity,
        queryEntity?: string,
    ): void {
        this.documentPreviewService.openPreviewModal({
            title: `Preview - ${this.getDisplayName(entityType)}`,
            entityId: entityId,
            entityType: entityType,
            sourceType: entityType,
            activeDocument: document,
            hideFileList: true,
            queryEntity: queryEntity || entityType,
            mode: DocumentPreviewMode.PreviewOnly,
        });
    }

    private onDownloadDocument(
        entityId: string,
        queryEntity: string,
        fileName?: string,
    ): void {
        this.filesFacadeService.downloadContextualFile(
            queryEntity,
            entityId,
            fileName,
        );
    }

    private getDisplayName(entityType: SystemEntity): string {
        const entityDisplayNamePipe = new EntityDisplayNamePipe(
            this.translationsService,
        );
        return entityDisplayNamePipe.transform(entityType);
    }

    private thresholdCellRenderer(
        viewColumn: ViewColumn,
        colDef: WdxColDef,
        thresholds: ViewThreshold[],
    ): CellRendererSelectorFunc {
        return (params: ICellRendererParams) => {
            const rawValue = params.data?.[viewColumn.path];

            if (rawValue == null || rawValue === '') {
                return;
            }

            const value = rawValue?.value ?? rawValue?.baseValue ?? rawValue;

            const thresholdResult = thresholds?.find((threshold) =>
                this.matchesThreshold(value, threshold),
            )?.result;

            const formattedValue =
                typeof colDef.valueFormatter === 'function'
                    ? colDef.valueFormatter({
                          ...params,
                          value,
                      } as ValueFormatterParams)
                    : value;

            const text =
                formattedValue != null && formattedValue !== ''
                    ? formattedValue
                    : value;

            return {
                component: IconWithTextCellRendererComponent,
                params: {
                    value: {
                        text,
                        icon: this.getThresholdIcon(value),
                        classes: `fw-bold ${this.getThresholdCellClasses(thresholdResult) || ''}`,
                    } as IconWithTextCellRendererParams,
                },
            };
        };
    }

    private getThresholdIcon(value: number): string {
        if (value > 0) {
            return UTILS_ICON_ARROW_UP;
        }

        if (value < 0) {
            return UTILS_THIN_ARROW_DOWN;
        }

        return UTILS_ICON_CHECK;
    }

    private formatDurationValue(value: number): string {
        const durationValue =
            this.businessDayLengthHours && this.businessDayLengthHours > 0
                ? value / (this.businessDayLengthHours * 3600)
                : value / 3600;
        const formattedDuration =
            this.numberService.formatNumber(durationValue, this.localeCode, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }) || '';

        if (this.businessDayLengthHours && this.businessDayLengthHours > 0) {
            const daysLabel = this.translationsService.translateTokenisedString(
                'ENUM_DURATIONTYPE_DAYS',
            );

            return `${formattedDuration} ${daysLabel}`;
        }

        const hoursLabel = this.translationsService.translateTokenisedString(
            'ENUM_DURATIONTYPE_HOURS',
        );

        return `${formattedDuration} ${hoursLabel}`;
    }

    private matchesThreshold(
        value: number,
        threshold?: ViewThreshold,
    ): boolean {
        switch (threshold.operator) {
            case ComparisonOperator.Gt:
                return value > threshold.value;
            case ComparisonOperator.Lt:
                return value < threshold.value;
            case ComparisonOperator.Eq:
                return value === threshold.value;
            default:
                return false;
        }
    }

    private getThresholdCellClasses(result: Rag): string | undefined {
        switch (result) {
            case Rag.Red:
                return 'text-danger';
            case Rag.Green:
                return 'text-success';
            default:
                return undefined;
        }
    }

    private selfCallbackAndUrl(
        config: {
            formName?:
                | null
                | string
                | ((params: ICellRendererParams) => string | undefined);
            hasDetailPage?: boolean;
            parentRoute?: string[];
            isEmbeddedPage?: boolean;
            disableSelfLink?: boolean;
        },
        entityId: string,
        entityType: SystemEntity,
        params: ICellRendererParams,
    ): { callback?: () => void; url?: string[] } {
        if (config.disableSelfLink) {
            return {};
        }
        const formId =
            typeof config.formName === 'function'
                ? config.formName(params)
                : config.formName;
        const callback =
            !config.hasDetailPage && formId
                ? () => {
                      this.dynamicFormsManagementService.openDynamicFormModal({
                          formId,
                          entityId,
                      });
                  }
                : undefined;

        if (callback || (!config.hasDetailPage && !config.isEmbeddedPage)) {
            return { callback };
        }

        if (config.isEmbeddedPage) {
            let url = this.entityRouteService.routeForEntity(
                entityType,
                entityId,
            );
            url = url?.length
                ? url
                : this.navigationService.getRouteForEntityDetailPage(
                      entityType,
                      entityId,
                  );
            return { url };
        }

        const url = this.entityRouteService.isLegacyEntityRoute(entityType)
            ? this.entityRouteService.routeForEntity(entityType, entityId)
            : [
                  ...(config.parentRoute
                      ? ['/', ...config.parentRoute]
                      : ['..']),
                  entityId,
              ];

        return { url };
    }
}
